import { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { TabView, TabPanel } from "primereact/tabview";
import { getDatosCliente } from "../../../../service/AffariService";
import { useOffline } from "../../../../offline/OfflineContext";
import { useGlobalContext } from "../../../../Config";
import { buscarDeudaClienteOffline } from "../../../../offline/offlineCatalogoService";

const DeudaDialog = ({ visible, onCancel, codigo }) => {
  const [deudas, setDeudas] = useState({ vencidas: [], vigentes: [] });
  const [loading, setLoading] = useState(false);
  const { offlineMode } = useOffline();
  const { isDinar } = useGlobalContext();

  useEffect(() => {
    if (visible && codigo) {
      setLoading(true);
      setDeudas({ vencidas: [], vigentes: [] });

      const cargarDeuda = async () => {
        if (!offlineMode) {
          const resp = await getDatosCliente(codigo);

          const normalizarDeudaOnline = (item) => ({
            ...item,
            saldo: Number(item?.saldo || 0),
            dias: Number(item?.atraso ?? item?.dias ?? 0),
            vendedor:
              item?.vendedor?.descripcion ||
              item?.vendedor?.nombre ||
              item?.codigoVendedor ||
              (typeof item?.vendedor === "string" ? item.vendedor : ""),
          });

          const vencidas = Array.isArray(resp?.deudas?.vencidas)
            ? resp.deudas.vencidas.map(normalizarDeudaOnline)
            : [];
          let vigentes = Array.isArray(resp?.deudas?.vigentes)
            ? resp.deudas.vigentes.map(normalizarDeudaOnline)
            : [];

          // DINAR mantiene el saldo de factura en dato_cliente aunque no siempre
          // existan filas desglosadas en deuda_cliente. En ese caso mostramos el
          // saldo real disponible como deuda vigente, sin inventar documentos.
          const saldoFactura = Number(
            resp?.dato?.saldoFactura ?? resp?.dato?.saldo_factura ?? 0,
          );
          if (
            isDinar &&
            vencidas.length === 0 &&
            vigentes.length === 0 &&
            Number.isFinite(saldoFactura) &&
            saldoFactura > 0
          ) {
            vigentes = [
              {
                tipo: "SALDO FACTURA",
                numero: "",
                vendedor: "",
                fecha: "",
                saldo: saldoFactura,
                dias: 0,
              },
            ];
          }

          return { vencidas, vigentes };
        }

        const deuda = await buscarDeudaClienteOffline(codigo);

        const normalizada = deuda.map((item) => ({
          ...item,
          saldo: Number(item.saldo || 0),
          dias: Number(item.atraso || 0),
        }));

        return {
          vencidas: normalizada.filter((item) => Number(item.dias) > 0),
          vigentes: normalizada.filter((item) => Number(item.dias) <= 0),
        };
      };

      cargarDeuda()
        .then(setDeudas)
        .catch((error) => {
          console.error("Error fetching debt data:", error);
        })
        .finally(() => setLoading(false));
    }
  }, [visible, codigo, offlineMode, isDinar]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
    }).format(value);
  };

  const calculateTotal = (items) => {
    if (!items) return 0;
    return items.reduce((acc, item) => acc + (item.saldo || 0), 0);
  };

  const footer = (items) => {
    return (
      <div className="flex justify-content-start font-bold">
        Total: {formatCurrency(calculateTotal(items))}
      </div>
    );
  };

  const renderTable = (items) => {
    return (
      <DataTable
        value={items}
        emptyMessage="No se encontraron deudas."
        showGridlines
        stripedRows
        footer={footer(items)}
        size="small"
        scrollable
        scrollHeight="400px"
      >
        <Column
          header="#"
          body={(data, options) => options.rowIndex + 1}
          style={{ width: "3rem" }}
        />
        <Column field="tipo" header="Tipo" />
        <Column field="numero" header="Numero" />
        <Column field="vendedor" header="Vendedor" />
        <Column field="fecha" header="Fecha" />
        <Column
          field="saldo"
          header="Saldo"
          body={(rowData) => formatCurrency(rowData.saldo)}
        />
        <Column field="dias" header="Dias" />
      </DataTable>
    );
  };

  return (
    <Dialog
      header={`Deuda Cliente`}
      visible={visible}
      className="p-fluid custom-dialog"
      modal
      onHide={onCancel}
      dismissableMask={true}
    >
      {loading ? (
        <div className="flex justify-content-center">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
        </div>
      ) : (
        <TabView>
          <TabPanel header="Vencidos">{renderTable(deudas.vencidas)}</TabPanel>
          <TabPanel header="Vigentes">{renderTable(deudas.vigentes)}</TabPanel>
        </TabView>
      )}
    </Dialog>
  );
};

export default DeudaDialog;
